import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  Plus, 
  Search, 
  Users,
  User,
  Phone,
  Mail,
  MapPin,
  DollarSign,
  Trash2,
  Edit,
  Crown
} from "lucide-react";
import { DialerActions } from "../components/dialer/DialerComponents";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BuyersPage({ user, subscription, onLogout }) {
  const { mainClass } = useSidebar();
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingBuyer, setEditingBuyer] = useState(null);
  
  const tier = subscription?.tier || user?.subscription_tier || "starter";

  const [formData, setFormData] = useState({
    name: "",
    buy_type: "cash",
    preferred_strategy: "",
    min_price: "",
    max_price: "",
    preferred_areas: "",
    phone: "",
    email: "",
    notes: ""
  });

  const buyTypes = [
    { value: "cash", label: "Cash Buyer" },
    { value: "financing", label: "Financing" },
    { value: "creative", label: "Creative Financing" }
  ];

  useEffect(() => {
    fetchBuyers();
  }, []);

  const fetchBuyers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/buyers`, { withCredentials: true });
      setBuyers(response.data);
    } catch (error) {
      console.error("Failed to fetch buyers:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        name: formData.name,
        buy_type: formData.buy_type,
        preferred_strategy: formData.preferred_strategy || null,
        min_price: parseFloat(formData.min_price) || 0,
        max_price: parseFloat(formData.max_price) || 10000000,
        preferred_areas: formData.preferred_areas.split(',').map(a => a.trim()).filter(Boolean),
        phone: formData.phone || null,
        email: formData.email || null,
        notes: formData.notes || null
      };

      if (editingBuyer) {
        await axios.put(`${BACKEND_URL}/api/buyers/${editingBuyer.buyer_id}`, payload, { withCredentials: true });
        toast.success("Buyer updated");
      } else {
        await axios.post(`${BACKEND_URL}/api/buyers`, payload, { withCredentials: true });
        toast.success("Buyer added");
      }
      
      setShowModal(false);
      resetForm();
      fetchBuyers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save buyer");
    }
  };

  const handleDelete = async (buyerId) => {
    if (!window.confirm("Delete this buyer?")) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/buyers/${buyerId}`, { withCredentials: true });
      toast.success("Buyer deleted");
      fetchBuyers();
    } catch (error) {
      toast.error("Failed to delete buyer");
    }
  };

  const handleEdit = (buyer) => {
    setEditingBuyer(buyer);
    setFormData({
      name: buyer.name,
      buy_type: buyer.buy_type,
      preferred_strategy: buyer.preferred_strategy || "",
      min_price: buyer.min_price.toString(),
      max_price: buyer.max_price.toString(),
      preferred_areas: buyer.preferred_areas.join(', '),
      phone: buyer.phone || "",
      email: buyer.email || "",
      notes: buyer.notes || ""
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingBuyer(null);
    setFormData({
      name: "",
      buy_type: "cash",
      preferred_strategy: "",
      min_price: "",
      max_price: "",
      preferred_areas: "",
      phone: "",
      email: "",
      notes: ""
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const filteredBuyers = buyers.filter(b => 
    b.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.preferred_areas.some(a => a.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-black">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className={`${mainClass} p-4 md:p-8 transition-all duration-300`} data-testid="buyers-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl font-bold text-white uppercase">Buyer List</h1>
            <p className="text-white/40 mt-1 text-sm">Track and manage verified buyers in your investment network.</p>
          </div>
          
          <Button 
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="gold-gradient-btn rounded-none font-bold uppercase tracking-wide"
            data-testid="add-buyer-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Buyer
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-md mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/70" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white"
            placeholder="Search by name or area..."
          />
        </div>

        {/* Buyers Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-10 h-10 border-4 border-gold border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredBuyers.length === 0 ? (
          <div className="text-center py-16 bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08]">
            <Users className="w-16 h-16 text-white/70 mx-auto mb-4" />
            <h3 className="text-xl font-heading font-bold text-white mb-2">
              {searchTerm ? "No Buyers Found" : "No Buyers Yet"}
            </h3>
            <p className="text-white/70 mb-6">
              {searchTerm ? "Try a different search term" : "Add buyers to your network"}
            </p>
            {!searchTerm && (
              <Button 
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
                className="gold-gradient-btn rounded-none font-bold"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Your First Buyer
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredBuyers.map((buyer, idx) => (
              <div
                key={buyer.buyer_id}
                className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-5 card-hover animate-fade-in"
                style={{ animationDelay: `${idx * 0.05}s` }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/[0.08] rounded-full flex items-center justify-center">
                      <span className="text-lg font-bold text-gold">{buyer.name.charAt(0)}</span>
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-white">{buyer.name}</h3>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs px-2 py-0.5 bg-gold/10 text-gold uppercase">
                          {buyer.buy_type}
                        </span>
                        {buyer.preferred_strategy && (
                          <span className="text-xs px-2 py-0.5 bg-white/[0.06] text-white/60">
                            {buyer.preferred_strategy.replace('_', ' & ').replace('fix & flip', 'Fix & Flip').replace('mixed use', 'Mixed Use').replace('rental', 'Rental').replace('wholesale', 'Wholesale').replace('development', 'Development').replace('land', 'Land')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleEdit(buyer)}
                      className="p-1.5 text-white/70 hover:text-gold transition-all duration-300"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(buyer.buyer_id)}
                      className="p-1.5 text-white/70 hover:text-[#FF3B30] transition-all duration-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-white/70">
                    <DollarSign className="w-4 h-4" />
                    <span className="font-mono">
                      {formatCurrency(buyer.min_price)} - {formatCurrency(buyer.max_price)}
                    </span>
                  </div>
                  
                  {buyer.phone && (
                    <div className="flex items-center gap-2 text-white/70">
                      <Phone className="w-4 h-4" />
                      <span>{buyer.phone}</span>
                    </div>
                  )}
                  
                  {/* Dialer Actions */}
                  {buyer.phone && (
                    <div className="pt-2 mt-2 border-t border-white/[0.08]">
                      <DialerActions
                        phoneNumber={buyer.phone}
                        contactId={buyer.buyer_id}
                        contactType="buyer"
                        contactName={buyer.name}
                        size="sm"
                        showLabels={false}
                      />
                    </div>
                  )}
                  
                  {buyer.email && (
                    <div className="flex items-center gap-2 text-white/70">
                      <Mail className="w-4 h-4" />
                      <span className="truncate">{buyer.email}</span>
                    </div>
                  )}
                  
                  {buyer.preferred_areas.length > 0 && (
                    <div className="flex items-start gap-2 text-white/70">
                      <MapPin className="w-4 h-4 mt-0.5" />
                      <div className="flex flex-wrap gap-1">
                        {buyer.preferred_areas.slice(0, 3).map((area, i) => (
                          <span key={i} className="px-2 py-0.5 bg-white/[0.08] text-xs">
                            {area}
                          </span>
                        ))}
                        {buyer.preferred_areas.length > 3 && (
                          <span className="text-xs text-white/70">
                            +{buyer.preferred_areas.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Buyer Modal */}
        <Dialog open={showModal} onOpenChange={(open) => {
          setShowModal(open);
          if (!open) resetForm();
        }}>
          <DialogContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-white uppercase">
                {editingBuyer ? "Edit Buyer" : "Add Buyer"}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white/70">Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="bg-black border-white/[0.08] text-white"
                    placeholder="John Smith"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-white/70">Buyer Type</Label>
                  <Select 
                    value={formData.buy_type} 
                    onValueChange={(v) => setFormData({...formData, buy_type: v})}
                  >
                    <SelectTrigger className="bg-black border-white/[0.08] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08]">
                      {buyTypes.map(t => (
                        <SelectItem key={t.value} value={t.value} className="text-white hover:bg-white/[0.06]">
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white/70">Min Price</Label>
                  <Input
                    type="number"
                    value={formData.min_price}
                    onChange={(e) => setFormData({...formData, min_price: e.target.value})}
                    className="bg-black border-white/[0.08] text-white"
                    placeholder="50000"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-white/70">Max Price</Label>
                  <Input
                    type="number"
                    value={formData.max_price}
                    onChange={(e) => setFormData({...formData, max_price: e.target.value})}
                    className="bg-black border-white/[0.08] text-white"
                    placeholder="500000"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-white/70">Preferred Strategy</Label>
                <Select 
                  value={formData.preferred_strategy} 
                  onValueChange={(v) => setFormData({...formData, preferred_strategy: v})}
                >
                  <SelectTrigger className="bg-black border-white/[0.08] text-white">
                    <SelectValue placeholder="Select strategy..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08]">
                    {[
                      { value: "fix_flip", label: "Fix & Flip" },
                      { value: "rental", label: "Rental" },
                      { value: "wholesale", label: "Wholesale" },
                      { value: "development", label: "Development" },
                      { value: "land", label: "Land" },
                      { value: "mixed_use", label: "Mixed Use" },
                    ].map(s => (
                      <SelectItem key={s.value} value={s.value} className="text-white hover:bg-white/[0.06]">
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white/70">Phone</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="bg-black border-white/[0.08] text-white"
                    placeholder="(555) 123-4567"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-white/70">Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="bg-black border-white/[0.08] text-white"
                    placeholder="buyer@email.com"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-white/70">Preferred Areas (comma-separated)</Label>
                <Input
                  value={formData.preferred_areas}
                  onChange={(e) => setFormData({...formData, preferred_areas: e.target.value})}
                  className="bg-black border-white/[0.08] text-white"
                  placeholder="Dallas, Fort Worth, Austin"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-white/70">Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="bg-black border-white/[0.08] text-white"
                  placeholder="Additional notes about this buyer..."
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowModal(false)}
                  className="border-white/[0.1] text-white/70 rounded-none"
                >
                  Cancel
                </Button>
                <Button type="submit" className="gold-gradient-btn rounded-none font-bold" data-testid="save-buyer-btn">
                  {editingBuyer ? "Update Buyer" : "Add Buyer"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
